import { useCallback } from 'react';
import { uniq } from 'lodash-es';
import {
  useMakeConversationCache,
  useGetConversationInfo,
  LocalMessage,
  removeFakeConversationOrderSn,
  getFakeConversationOrderSn,
  useCurrentConversationIdValue,
  useConversationIdListValue,
  useSetConversationIdListValue,
  useFetchConversationInfo,
  useSetCurrentConversationIdValue,
} from 'hooks/business/conversation';

import { getTalkInfo } from 'services/api/conversation.convList';
import { UpdateOfferStatusBody } from 'services/api/offer';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { NimSytemUnreadCountMessage } from 'services/nim/eventBus';
import { useUnreadCountTimeMapStateValue, useSetUnreadCountTimeMapStateValue } from './timeMap';
import { useConversationRead } from '../useConversationRead';
import { useConversationCategory } from 'hooks/business/conversationsHooks/useConversationCategory';

import posthog from '@leyan/lytics';
import { useSetConversation, useGetConversationById } from '../lruConversation';
import { messageTypes } from './useCurrentChat';
import { useUpdateMessage } from '../message';
import { logger } from 'services/logger';
import { isIdEqual } from 'utils/is';
import { useMemoizedFn } from 'ahooks';
import { useAuthorization } from 'components/Authorization';
import chatttSDK from 'services/chattt';
import { notifyRef } from 'components/Nim';
import focusChatttClient from 'utils/focusChatttClient';

export type ConversationMsg = Conversation.Message & { isSupervise: boolean };
export const getFailContent = (
  msg: Omit<ConversationMsg, 'isSupervise'>,
): Conversation.FailContent | undefined => {
  if (msg.messageType === 'TEXT') {
    return msg.textContent?.failCause;
  }
  if (msg.messageType === 'IMAGE') {
    return msg.imageContent?.failCause;
  }
  if (msg.messageType === 'STICKER') {
    return msg.stickerContent?.failCause;
  }
  if (msg.messageType === 'ORDER') {
    return msg.orderContent?.failCause;
  }
  if (msg.messageType === 'ITEM') {
    return msg.itemContent?.failCause;
  }
  if (msg.messageType === 'VOUCHER') {
    return msg.voucherContent?.failCause;
  }
  if (msg.messageType === 'TEMPLATE') {
    return msg.templateContent?.failCause;
  }
  if (msg.messageType === 'FOLLOWER') {
    return msg.followerContent?.failCause;
  }
  if (msg.messageType === 'TEXT_VIDEO') {
    return msg.textVideoContent?.failCause;
  }
  if (msg.messageType === 'VIDEO') {
    return msg.videoContent?.failCause;
  }
  return undefined;
};

/** 对话消息作用到会话标签上, 当前接待会话时执行已读操作 */
export function useComingMsgToConversation() {
  const setConversation = useMakeConversationCache('set');
  const conversationRead = useConversationRead();
  const offerMsgUpdate = useOfferMsgUpdate();
  const fetchConversationInfo = useFetchConversationInfo(true);
  const getConversationById = useGetConversationById();

  const currentConversationId = useCurrentConversationIdValue();
  const conversationIdList = useConversationIdListValue();
  const setConversationIdList = useSetConversationIdListValue();
  const category = useConversationCategory();

  const { t } = useTranslate();

  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const setCurId = useSetCurrentConversationIdValue();

  return useMemoizedFn(async (msg: ConversationMsg, failContent?: Conversation.FailContent) => {
    try {
      const {
        msgId,
        externalId,
        messageType,
        textContent,
        imageWithTextContent,
        sendTime,
        msgFromType,
        isSupervise,
        status,
        sysPauseAiContent,
        sysAsteriskContent,
        sysAutoMarkSalesStatusContent,
      } = msg;
      if (status === 'RECALL' || !messageTypes.includes(messageType)) {
        return;
      }

      if (
        msg.offerContent?.buyerMsgId &&
        msg.offerContent?.status &&
        ['accepted', 'rejected'].includes(msg.offerContent.status)
      ) {
        offerMsgUpdate(
          msg.offerContent?.buyerMsgId,
          msg.offerContent.status as UpdateOfferStatusBody['operation'],
        );
      }

      const failCause = failContent || getFailContent(msg);
      const needSyncConvInfo = [
        'SYS_BLACKLIST',
        'SYS_TRANSFER',
        'SYS_ASSIGN',
        'SYS_ASSIGN_CONTEXT',
      ].includes(messageType);

      let convInfo = getConversationById(msg.conversationId);
      if (!convInfo || needSyncConvInfo || convInfo.mockExternal || convInfo.channel === 'WA_BIZ') {
        try {
          convInfo = await fetchConversationInfo(msg.conversationId);
        } catch (e: any) {
          logger.error(
            `[useComingMsgToConversation] batchGetTalk error: ${e?.message || 'unknown error'}`,
            {
              conversationId: msg.conversationId,
            },
          );
        }
      }

      if (!convInfo) {
        return;
      }

      if (!convInfo.mockExternal && getFakeConversationOrderSn(convInfo.conversationId)) {
        removeFakeConversationOrderSn(convInfo.conversationId);
        posthog.capture('$sourceContent', {
          type: 'remove',
          conversationId: convInfo.conversationId,
        });
      }

      if (
        category !== 'all' &&
        !conversationIdList.includes(convInfo.conversationId) &&
        !isIdEqual(convInfo.conversationId, currentConversationId)
      ) {
        setConversationIdList((list) => uniq([...list, convInfo.conversationId]));
      }

      const { lastMsgTime, lastMsgType, lastMsgId, lastExternalMsgId } = convInfo;

      let isBiggerExternalId: boolean;
      try {
        isBiggerExternalId = Boolean(BigInt && BigInt(externalId) > BigInt(lastExternalMsgId));
      } catch (err) {
        isBiggerExternalId = false;
      }

      const isLastMsg =
        parseInt(`${sendTime}`, 10) > parseInt(lastMsgTime, 10) || isBiggerExternalId;

      /**
       * 只处理 inReception 为 true 的情况
       */
      const inReception = msg.belongTalk?.inReception || convInfo.inReception;

      if (!isLastMsg && messageType !== 'SYS_TRANSFER' && messageType !== 'SYS_ASSIGN') {
        // 最后一条消息为 BUNDLE_MESSAGE 时不算待处理，消息推送顺序错乱时，需处理 processed 状态。
        if (lastMsgType === 'BUNDLE_MESSAGE') {
          setConversation({
            ...convInfo,
            processed: msg.belongTalk?.processed ?? convInfo.processed,
            inReception,
          });
        }

        return;
      }

      let convInfoChg = {
        ...convInfo,
      };
      let { botMark } = convInfo;
      if (
        msg.msgFromType === 'BOT' &&
        msg.botReplyType !== 'MARKETING' &&
        msg.botReplyType !== 'TIMEOUT_AUTO_REPLY'
      ) {
        botMark = true;
      }

      let lastMsgContent = '';
      let localExtra;
      if (messageType === 'TEXT') {
        lastMsgContent = textContent?.text as string;
      } else if (messageType === 'IMAGE_WITH_TEXT') {
        lastMsgContent = imageWithTextContent?.text || '';
      }

      if (
        msgFromType !== 'SYSTEM' ||
        !lastMsgTime ||
        lastMsgTime === '0' ||
        messageType === 'SYS_TRANSFER' ||
        messageType === 'SYS_REMIND_TRIGGER' ||
        messageType === 'SYS_ASTERISK_STAR' ||
        messageType === 'SYS_MANUAL_MARK_SALES_STATUS' ||
        messageType === 'SYS_NEG_RATE' ||
        messageType === 'SYS_CANCEL_ORDER'
      ) {
        convInfoChg = {
          ...convInfoChg,
          lastMsgId: msgId || lastMsgId,
          lastExternalMsgId: externalId || lastExternalMsgId,
          lastMsgTime: `${sendTime}`,
          lastMsgType: messageType,
          lastMsgFromType: msgFromType as Conversation.MsgFromType,
        };
      }

      if (messageType === 'SYS_ASSIGN' || messageType === 'SYS_ASSIGN_CONTEXT') {
        convInfoChg = { ...convInfo, inReception };
      } else {
        let processed;
        if (failCause && failCause.failType !== 'D') {
          processed = false;
        } else if (needSyncConvInfo) {
          processed = convInfo.processed;
        } else {
          processed = msg?.belongTalk?.processed ?? convInfo.processed;
        }
        convInfoChg = {
          ...convInfoChg,
          lastMsgContent,
          unreadCount: failCause && failCause.failType !== 'D' ? 1 : convInfo.unreadCount,
          botMark,
          receptionName: convInfo.receptionName,
          superviseMark: isSupervise || convInfo.superviseMark,
          localExtra,
          processed,
          inReception,
          failCause,
          fstUnprocessedMsgTimestamp: processed
            ? '0'
            : convInfoChg.fstUnprocessedMsgTimestamp === '0'
            ? `${Date.now()}`
            : convInfoChg.fstUnprocessedMsgTimestamp,
        };
      }
      switch (messageType) {
        case 'SYS_ASTERISK_STAR': {
          convInfoChg = {
            ...convInfo,
          };
          if (sysAsteriskContent?.color) {
            convInfoChg.colorAsterisk = sysAsteriskContent.color;
          }
          break;
        }
        case 'SYS_AUTO_MARK_SALES_STATUS': {
          convInfoChg = {
            ...convInfo,
          };
          if (sysAutoMarkSalesStatusContent?.salesStatus) {
            convInfoChg.salesStatus = sysAutoMarkSalesStatusContent.salesStatus as any;
          }
          break;
        }
        case 'SYS_PAUSE_AI_STOP':
        case 'SYS_PAUSE_AI_START':
        case 'SYS_PAUSE_AI_EXPIRE':
        case 'SYS_NEG_RATE_PAUSE_AI_START':
        case 'SYS_NEG_RATE_PAUSE_AI_EXPIRE':
        case 'SYS_NEG_RATE_PAUSE_AI_PROCESS': {
          const isPuased =
            messageType === 'SYS_PAUSE_AI_START' || messageType === 'SYS_NEG_RATE_PAUSE_AI_START';
          if (sysPauseAiContent) {
            const { endMillis } = sysPauseAiContent;
            convInfoChg = {
              ...convInfo,
              pauseAiReply: isPuased,
              pauseAiReplyEndMillis: endMillis,
            };
          }
          break;
        }
        case 'SYS_PAUSE_MK_RR_STOP':
        case 'SYS_PAUSE_MK_RR_START':
        case 'SYS_PAUSE_MK_RR_EXPIRE':
          if (sysPauseAiContent) {
            const { endMillis } = sysPauseAiContent;
            convInfoChg = {
              ...convInfo,
              pauseMkRr: messageType === 'SYS_PAUSE_MK_RR_START',
              pauseMkRrEndMillis: endMillis,
            };
          }
          break;
        case 'SYS_REMIND_TRIGGER':
        case 'SYS_REMIND_AUTO_CLOSE':
          {
            let remindTriggerContent = [] as Conversation.MsgSysRemindTrigger[];
            if (convInfo.remindTriggerContent) {
              remindTriggerContent = [...convInfo.remindTriggerContent];
            }

            if (messageType === 'SYS_REMIND_TRIGGER' && msg.sysRemindTriggerContent) {
              remindTriggerContent.push(msg.sysRemindTriggerContent);
            } else if (
              messageType === 'SYS_REMIND_AUTO_CLOSE' &&
              msg.sysRemindAutoCloseContent?.trigger
            ) {
              remindTriggerContent.some((v, i) => {
                const { trigger } = msg.sysRemindAutoCloseContent!;
                if (v.scene === trigger.scene && v.condition === trigger.condition) {
                  remindTriggerContent.splice(i, 1);
                  return true;
                }
                return false;
              });
            }

            convInfoChg = {
              ...convInfoChg,
              remindMark: remindTriggerContent.length > 0,
              remindTriggerContent,
            };
          }
          break;
        case 'SYS_REMIND_MANUAL_CLOSE':
          convInfoChg = {
            ...convInfoChg,
            remindMark: false,
            remindTriggerContent: [],
          };
          break;
        case 'SYS_FAQ_QUESTION': {
          convInfoChg = {
            ...convInfoChg,
            lastMsgContent: msg?.sysFaqQuestionContent?.question ?? '',
          };

          break;
        }
        default:
          break;
      }

      // // `机器人,客服,超管`回复设置未读数为0, 不清除标记
      // if (msg.msgFromType === 'BOT' || msg.msgFromType === 'ASSISTANT') {
      //   await conversationRobotReplyUnreadClear(convInfoChg);
      // }
      // 当前接待对话,清除标记
      if (isIdEqual(msg.conversationId, currentConversationId)) {
        if (category !== 'all') {
          conversationRead(convInfoChg);
        } else {
          setConversation(convInfoChg);
        }
      } else {
        setConversation(convInfoChg);
      }

      if (msg.messageType === 'SYS_ASSIGN_CONTEXT') {
        const { toAssistantDest: { assistantId, voiceNotify } = {} } =
          msg.sysAssignContextContent || {};
        if (`${payload?.id}` === `${assistantId}` && voiceNotify) {
          if (chatttSDK?.emitter?.emit) {
            chatttSDK?.emitter.emit('new_message', message);
          }
          notifyRef.current(
            {
              title: t('notification.NEW_MESSAGE_REMINDER'),
              content: `[${t('notification.SYSTEM_MESSAGE')}]`,
              id: msg.msgId,
              conversationId: msg.conversationId,
            },
            {
              onClick: () => {
                focusChatttClient();
                setCurId(msg.conversationId);
              },
            },
          );
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  });
}

/** 发送消息作用到会话标签上 */
export function useSendingMsgMakeToConversation() {
  const setConversation = useMakeConversationCache('set');
  const getConversationInfo = useGetConversationInfo(true);
  const curId = useCurrentConversationIdValue();

  return useMemoizedFn(async (msg: LocalMessage, failCause?: Conversation.FailContent) => {
    try {
      const convInfo = await getConversationInfo(msg.conversationId);
      const { messageType, msgFromType, textContent, sendTime, templateContent } = msg;
      const getLastMsgContent = () => {
        if (messageType === 'TEXT') {
          return textContent?.text ?? '';
        }
        if (messageType === 'TEMPLATE') {
          return templateContent?.text ?? '';
        }
        return '';
      };
      const convInfoChg = {
        ...convInfo,
        lastMsgFromType: msgFromType,
        lastMsgType: messageType,
        lastMsgContent: getLastMsgContent(),
        lastMsgTime: sendTime as string,
        unreadCount: failCause && curId !== convInfo.conversationId ? convInfo.unreadCount : 0,
        failCause,
        processed: failCause && curId !== convInfo.conversationId ? false : convInfo.processed,
      };
      setConversation(convInfoChg as Conversation.Conversation);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  });
}

/** 系统操作失败消息同步到会话标签 */
export function useSysFailMsgToConversation() {
  const setConversation = useMakeConversationCache('set');
  const { t } = useTranslate();

  return useCallback(
    async (conversationId: string) => {
      try {
        let convInfo;
        try {
          convInfo = await getTalkInfo({ conversationId });
        } catch (e) {
          message.error(t('api.getTalkInfo'));
          throw e;
        }
        setConversation(convInfo);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    [setConversation, t],
  );
}

/** 已读未读消息同步到会话标签 */
export function useReadStatusMsgToConversation() {
  const setConversation = useSetConversation();
  const getConversationById = useGetConversationById();

  const unreadCountTimeMap = useUnreadCountTimeMapStateValue();

  const setUnreadCountTimeMap = useSetUnreadCountTimeMapStateValue();

  return (msg: NimSytemUnreadCountMessage) => {
    const {
      content: { conversationId, count, readTime, talkId },
    } = msg;

    const { [conversationId]: preReadTime } = unreadCountTimeMap;

    const conversation = getConversationById(String(conversationId));

    if (
      !conversation ||
      (String(talkId) !== String(conversation?.talkId) && String(talkId) !== '0') ||
      (preReadTime && preReadTime > readTime)
    ) {
      return;
    }

    setUnreadCountTimeMap((map) => {
      return {
        ...map,
        [conversationId]: readTime,
      };
    });

    setConversation(String(conversationId), {
      ...conversation,
      unreadCount: count,
    });
  };
}

/** 议价操作后更新消息状态 */
export function useOfferMsgUpdate() {
  const updateMessage = useUpdateMessage();

  return useCallback(
    (msgId: string, status: UpdateOfferStatusBody['operation']) => {
      updateMessage(msgId, (prev) => {
        if (!prev || !prev?.offerContent) {
          return undefined;
        }

        return {
          ...prev,
          offerContent: {
            ...prev.offerContent!,
            status,
          },
        };
      });
    },
    [updateMessage],
  );
}
